import React, { useState, useEffect, useRef, useCallback } from 'react';
import './TimezoneSelector.css';

/**
 * TimezoneSelector Component
 *
 * A user-friendly timezone selector based on Nielsen Norman Group research findings.
 *
 * Features:
 * - Automatic timezone detection
 * - Searchable dropdown (city, country, state, timezone name)
 * - Alphabetical ordering by city
 * - Full keyboard navigation
 * - Mobile-friendly
 * - Accessible (WCAG 2.1 Level AA)
 *
 * @param {Object} props
 * @param {Function} props.onChange - Callback function called when timezone is selected
 * @param {string} props.value - Selected timezone ID (e.g., 'America/Los_Angeles')
 * @param {string} props.name - Form field name
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.autoDetect - Enable automatic timezone detection (default: true)
 * @param {string} props.className - Additional CSS classes
 */
export function TimezoneSelector({
  onChange,
  value,
  name = 'timezone',
  placeholder = 'Select timezone...',
  autoDetect = true,
  className = ''
}) {
  const [timezones, setTimezones] = useState([]);
  const [filteredTimezones, setFilteredTimezones] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [detectedTimezone, setDetectedTimezone] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loading, setLoading] = useState(true);

  const containerRef = useRef(null);
  const searchFieldRef = useRef(null);
  const listContainerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Load timezone data from API
  useEffect(() => {
    const apiHost = process.env.REACT_APP_MAILBOTS_API_BASE || 'http://localhost';

    fetch(`${apiHost}/api/v1/timezones/`)
      .then(response => response.json())
      .then(data => {
        setTimezones(data.timezones || []);
        setFilteredTimezones(data.timezones || []);
        setLoading(false);
      })
      .catch(error => {
        console.error('Failed to load timezone data:', error);
        setLoading(false);
      });
  }, []);

  // Detect user's timezone
  useEffect(() => {
    if (!autoDetect || timezones.length === 0) return;

    try {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const detected = timezones.find(tz => tz.id === browserTz);

      if (detected) {
        setDetectedTimezone(detected);

        // Auto-select if no value is provided
        if (!value) {
          setSelectedTimezone(detected);
          if (onChange) {
            onChange({ target: { name, value: detected.id } }, detected);
          }
        }
      }
    } catch (error) {
      console.warn('Timezone detection not supported:', error);
    }
  }, [timezones, autoDetect, value, name, onChange]);

  // Set selected timezone from value prop
  useEffect(() => {
    if (value && timezones.length > 0) {
      const tz = timezones.find(t => t.id === value);
      if (tz) {
        setSelectedTimezone(tz);
      }
    }
  }, [value, timezones]);

  // Filter list based on search query
  const filterList = useCallback((query) => {
    if (!query || query.trim() === '') {
      setFilteredTimezones(timezones);
    } else {
      const lowerQuery = query.toLowerCase();
      // Split query into words and check if all words are found in searchText
      const queryWords = lowerQuery.trim().split(/\s+/);
      const filtered = timezones.filter(tz => {
        // All query words must be found in the searchText
        return queryWords.every(word => tz.searchText.indexOf(word) !== -1);
      });
      setFilteredTimezones(filtered);
    }
    setHighlightedIndex(-1);
  }, [timezones]);

  // Handle search input with debounce
  const handleSearchInput = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Debounce by 150ms
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      filterList(query);
    }, 150);
  };

  // Toggle dropdown open/close
  const toggle = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  // Open dropdown
  const open = () => {
    setIsOpen(true);
    // Focus search field after a brief delay to ensure DOM is updated
    setTimeout(() => {
      if (searchFieldRef.current) {
        searchFieldRef.current.focus();
      }
    }, 50);
  };

  // Close dropdown
  const close = () => {
    setIsOpen(false);
    setSearchQuery('');
    filterList('');
    setHighlightedIndex(-1);
  };

  // Select a timezone
  const selectTimezone = (timezone) => {
    setSelectedTimezone(timezone);

    if (onChange) {
      onChange({ target: { name, value: timezone.id } }, timezone);
    }

    close();
  };

  // Keyboard navigation helpers
  const highlightNext = () => {
    const visibleList = detectedTimezone ?
      filteredTimezones.filter(tz => tz.id !== detectedTimezone.id) :
      filteredTimezones;

    if (visibleList.length === 0) return;

    setHighlightedIndex(prev => {
      const newIndex = prev < visibleList.length - 1 ? prev + 1 : 0;
      scrollToHighlighted(newIndex);
      return newIndex;
    });
  };

  const highlightPrevious = () => {
    const visibleList = detectedTimezone ?
      filteredTimezones.filter(tz => tz.id !== detectedTimezone.id) :
      filteredTimezones;

    if (visibleList.length === 0) return;

    setHighlightedIndex(prev => {
      const newIndex = prev <= 0 ? visibleList.length - 1 : prev - 1;
      scrollToHighlighted(newIndex);
      return newIndex;
    });
  };

  const highlightFirst = () => {
    setHighlightedIndex(0);
    scrollToHighlighted(0);
  };

  const highlightLast = () => {
    const visibleList = detectedTimezone ?
      filteredTimezones.filter(tz => tz.id !== detectedTimezone.id) :
      filteredTimezones;

    const lastIndex = visibleList.length - 1;
    setHighlightedIndex(lastIndex);
    scrollToHighlighted(lastIndex);
  };

  const selectHighlighted = () => {
    const visibleList = detectedTimezone ?
      filteredTimezones.filter(tz => tz.id !== detectedTimezone.id) :
      filteredTimezones;

    if (highlightedIndex >= 0 && highlightedIndex < visibleList.length) {
      selectTimezone(visibleList[highlightedIndex]);
    }
  };

  const scrollToHighlighted = (index) => {
    if (!listContainerRef.current) return;

    const items = listContainerRef.current.querySelectorAll('.tz-list-item');
    if (items[index]) {
      items[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  // Keyboard event handlers
  const handleSelectFieldKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      open();
      highlightNext();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      open();
      highlightPrevious();
    }
  };

  const handleSearchFieldKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      highlightNext();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlightPrevious();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectHighlighted();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Home') {
      e.preventDefault();
      highlightFirst();
    } else if (e.key === 'End') {
      e.preventDefault();
      highlightLast();
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        close();
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Render timezone list item
  const renderListItem = (tz, isDetected = false) => {
    const isSelected = selectedTimezone && selectedTimezone.id === tz.id;
    const itemClasses = ['list-group-item', 'tz-list-item'];
    if (isDetected) itemClasses.push('tz-detected');
    if (isSelected) itemClasses.push('active');

    const visibleList = detectedTimezone ?
      filteredTimezones.filter(t => t.id !== detectedTimezone.id) :
      filteredTimezones;

    const itemIndex = visibleList.findIndex(t => t.id === tz.id);
    const isHighlighted = itemIndex === highlightedIndex && !isDetected;

    if (isHighlighted) itemClasses.push('highlighted');

    return (
      <div
        key={tz.id}
        className={itemClasses.join(' ')}
        data-tz-id={tz.id}
        role="option"
        aria-selected={isSelected}
        tabIndex={-1}
        onClick={() => selectTimezone(tz)}
      >
        <span className="tz-checkmark">✓</span>
        <span className="tz-item-text">
          <span className="tz-offset">({tz.offset})</span>
          {tz.timezoneName && (
            <>
              {' '}<span className="tz-timezone-name">{tz.timezoneName}</span>
            </>
          )}
          {' - '}
          <span className="tz-city">{tz.city}</span>
          {tz.country === 'United States' && tz.state && (
            <>
              {', '}<span className="tz-state">{tz.state}</span>
            </>
          )}
          {tz.country && tz.country !== 'United States' && (
            <>
              {', '}<span className="tz-country">{tz.country}</span>
            </>
          )}
        </span>
      </div>
    );
  };

  // Get display value
  const getDisplayValue = () => {
    if (selectedTimezone) {
      return selectedTimezone.displayName;
    }
    return placeholder;
  };

  const visibleList = detectedTimezone ?
    filteredTimezones.filter(tz => tz.id !== detectedTimezone.id) :
    filteredTimezones;

  return (
    <div className={`timezone-selector ${className}`} ref={containerRef}>
      {/* Selected Value Display */}
      <div
        className="tz-select-field form-control"
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={toggle}
        onKeyDown={handleSelectFieldKeyDown}
      >
        <span className={`tz-selected-value ${!selectedTimezone ? 'placeholder' : ''}`}>
          {getDisplayValue()}
        </span>
        <span className="tz-dropdown-arrow">▼</span>
      </div>

      {/* Dropdown Panel */}
      <div
        className="dropdown-menu tz-dropdown"
        role="listbox"
        aria-hidden={!isOpen}
        style={{ display: isOpen ? 'block' : 'none' }}
      >
        {/* Search Field */}
        <div className="tz-search-container">
          <input
            ref={searchFieldRef}
            type="text"
            className="tz-search-field form-control"
            placeholder="Search by city, country, timezone, or offset..."
            role="searchbox"
            aria-label="Search timezones"
            value={searchQuery}
            onChange={handleSearchInput}
            onKeyDown={handleSearchFieldKeyDown}
          />
          <span className="tz-search-icon">🔍</span>
        </div>

        {/* Timezone List */}
        <div className="list-group tz-list-container" ref={listContainerRef}>
          {loading && (
            <div className="list-group-item tz-loading">Loading timezones...</div>
          )}

          {!loading && detectedTimezone && (
            <>
              <div className="list-group-item tz-section-header">Detected Timezone</div>
              {renderListItem(detectedTimezone, true)}
              <div className="list-group-item tz-section-header">All Timezones</div>
            </>
          )}

          {!loading && visibleList.length > 0 && visibleList.map(tz => renderListItem(tz))}

          {!loading && visibleList.length === 0 && (
            <div className="list-group-item tz-no-results">
              No timezones found matching "<span className="tz-search-term">{searchQuery}</span>"
            </div>
          )}
        </div>
      </div>

      {/* Hidden input for form submission */}
      <input
        type="hidden"
        name={name}
        value={selectedTimezone ? selectedTimezone.id : ''}
        className="tz-hidden-input"
      />
    </div>
  );
}

export default TimezoneSelector;
